﻿window.ebiztextbox = {
    isTextTruncated: function (internalId, className) {
        const root = document.querySelector(`[data-ebiz-id="${internalId}"]`);
        if (!root) {
            return false;
        }

        const element = root.querySelector("." + className);
        if (!element) {
            return false;
        }

        // get the text and the stylings
        const value = element.value ?? element.textContent;
        const style = getComputedStyle(element);

        // measure the text width
        const canvas = globalThis.ebiztextbox._measureCanvas || (globalThis.ebiztextbox._measureCanvas = document.createElement("canvas"));
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return false;
        }
        ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        const letterSpacing = Number.parseFloat(style.letterSpacing) || 0;
        const textWidth = ctx.measureText(value).width + (letterSpacing * Math.max(value.length - 1, 0));

        // measure the available space
        const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
        const paddingRight = Number.parseFloat(style.paddingRight) || 0;
        const elementWidth = element.getBoundingClientRect().width;
        const availableWidth = elementWidth - paddingLeft - paddingRight;

        const isTruncated = textWidth > availableWidth;

        return isTruncated;
    },

    setCursorEnd: function (element) {
        if (element) {
            const input = element.querySelector(".k-maskedtextbox input.k-input-inner");
            if (input) {
                const trimmedLength = input.value.trimEnd().length;
                input.setSelectionRange(trimmedLength, trimmedLength);
            }
        }
        return false;
    },

    clickSubmit: function (id) {
        const btn = document.getElementById(id);
        if (btn) {
            btn.click();
        }
    },

    adjustLabelWidth: function (internalId) {
        const root = document.querySelector(`[data-ebiz-id="${internalId}"]`);
        if (!root) {
            console.warn("Root not found:", internalId);
            return;
        }

        const input = root.querySelector('.custom-label-class .k-input-inner');
        const label = root.querySelector('.custom-label-class .k-floating-label');

        if (input && label) {
            label.style.maxWidth = `${input.offsetWidth - 24}px`;
        }
    },

    registerResize: function (id, dotNetRef) {
        function handler() {
            window.ebiztextbox.adjustLabelWidth(id);
            const isLabelTruncated = window.ebiztextbox.isTextTruncated(id, "k-floating-label");
            const isInputTruncated = window.ebiztextbox.isTextTruncated(id, "k-input-inner");
            dotNetRef.invokeMethodAsync("OnTextTruncationChanged", isInputTruncated, isLabelTruncated);
        }

        handler();
        window.addEventListener("resize", handler);
        return {
            dispose: () => {
                window.removeEventListener("resize", handler);
            }
        };
    },

    // Blocks any keystroke/paste/drop that would insert a character outside `allowedChars` before
    // it ever reaches the DOM, so disallowed input (letters, stray punctuation) never renders even
    // momentarily \u2014 unlike filtering after the fact in .NET, which lets the bad character flash on
    // screen for a render cycle before the corrected value round-trips back down.
    restrictToAllowedCharacters: function (internalId, allowedChars) {
        const root = document.querySelector(`[data-ebiz-id="${internalId}"]`);
        if (!root) {
            return null;
        }

        const input = root.querySelector("input");
        if (!input) {
            return null;
        }

        const allowedSet = new Set(Array.from(allowedChars));

        function handler(e) {
            if (e.isComposing) {
                return; // let IME compose
            }

            const data = e.data;
            if (data === null || data === undefined) {
                return; // deletions, cut, etc. have no inserted text to check
            }

            for (const ch of data) {
                if (!allowedSet.has(ch)) {
                    e.preventDefault();
                    return;
                }
            }
        }

        input.addEventListener("beforeinput", handler);
        return {
            dispose: () => {
                input.removeEventListener("beforeinput", handler);
            }
        };
    },

};


// ---- Helpers ----
function formatAmex(value) {
    if (value.length <= 4) return value;
    if (value.length <= 10) return value.substring(0, 4) + " " + value.substring(4);
    return value.substring(0, 4) + " " + value.substring(4, 10) + " " + value.substring(10, 15);
}

function formatDefault(value) {
    return value.replace(/(.{4})/g, "$1 ").trim();
}

function formatCardNumber(value, isAmex) {
    return isAmex ? formatAmex(value) : formatDefault(value);
}

function getMaxDigits(digitsOnly) {
    return /^3[47]/.test(digitsOnly) ? 15 : 16; // Amex = 15, others = 16
}

function enforceMaxDigits(value, maxDigits) {
    return value.length > maxDigits ? value.slice(0, maxDigits) : value;
}

function digitsCount(str) {
    return str.replace(/\D/g, "").length;
}

function selectInputText(input) {
    input.select();
}

// Exposed for EBizValueSelector's JS interop: esbuild bundles these files into a minified IIFE,
// which renames bare top-level functions — anything invoked by name from .NET must be attached
// to the global scope explicitly (like window.ebiztextbox / window.cardFormatter below).
globalThis.selectInputText = selectInputText;
globalThis.formatNumericValue = formatNumericValue;

// The decimal separator the given Intl format uses ('.', ',', '٫', …).
function resolveDecimalSeparator(numberFormat) {
    const formatParts = numberFormat.formatToParts(12345.6);
    return formatParts.find(p => p.type === 'decimal')?.value ?? '.';
}

// Keeps only the characters a numeric entry can be built from: digits, sign, decimal separator.
function keepNumericChars(text, decimalSep) {
    let value = '';
    for (const ch of text) {
        if ((ch >= '0' && ch <= '9') || ch === '-' || ch === decimalSep) {
            value += ch;
        }
    }
    return value;
}

function notifyNumericValueChanged(dotNetRef, isPercent, value) {
    dotNetRef.invokeMethodAsync(isPercent ? 'OnPercentInputChanged' : 'OnInputChanged', value);
}

function formatNumericValue(input, dotNetRef, isPercent, locale, maxDecimals) {
    // Locale-aware masking: grouping/decimal separators come from the supplied locale (the app's
    // money layout culture) and the decimal cap from the active currency (0 for JPY, 3 for JOD).
    // Defaults preserve the historical en-US / two-decimal behavior for old callers.
    locale = locale || 'en-US';
    if (maxDecimals === undefined || maxDecimals === null) maxDecimals = 2;

    const numberFormat = new Intl.NumberFormat(locale);
    const decimalSep = resolveDecimalSeparator(numberFormat);

    const cursorPosition = input.selectionStart;
    const oldValue = input.value;

    let value = keepNumericChars(input.value, decimalSep);

    // If the cleaned value is empty or just contains separators, treat as 0
    if (value === '' || value === decimalSep || value === '-') {
        input.value = '';
        notifyNumericValueChanged(dotNetRef, isPercent, '');
        return;
    }

    const isNegative = value.startsWith('-');
    value = value.replaceAll(/-/g, '');
    const sepIndex = value.indexOf(decimalSep);
    let integerPart = sepIndex >= 0 ? value.slice(0, sepIndex) : value;
    const decimalPart = (sepIndex >= 0
        ? value.slice(sepIndex + decimalSep.length).replaceAll(decimalSep, '')
        : '').substring(0, maxDecimals);

    integerPart = numberFormat.format(integerPart === '' ? 0 : integerPart);
    let formattedValue = (isNegative ? '-' : '') + integerPart;

    if (sepIndex >= 0 && maxDecimals > 0) {
        formattedValue += decimalSep + decimalPart;
    }

    input.value = formattedValue;
    const diff = input.value.length - oldValue.length;
    const newPosition = cursorPosition + diff;
    input.setSelectionRange(newPosition, newPosition);

    notifyNumericValueChanged(dotNetRef, isPercent, input.value);
}
function calculateCursorPosition(rawValue, formatted, cursorPos, maxDigits) {
    let digitsBefore = rawValue.slice(0, cursorPos).replace(/\D/g, "").length;
    if (digitsBefore > maxDigits) digitsBefore = maxDigits;
    if (digitsBefore === 0) return 0;

    let digitsSeen = 0;
    for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
            digitsSeen++;
        }
        if (digitsSeen === digitsBefore) {
            return i + 1;
        }
    }
    return formatted.length; // fallback
}

// ---- Digit Box Logic ----
globalThis.EBizDigitBox = {
    initializeDigitBox: function (inputId, nextInputId, prevInputId, nextNextInputId) {
        const input = document.getElementById(inputId);
        if (!input) return;

        input.addEventListener('focus', function () {
            this.select();
        });

        input.addEventListener('keydown', function (e) {
            const caretStart = this.selectionStart;
            const caretEnd = this.selectionEnd;
            const isDigit = e.key >= '0' && e.key <= '9';
            const hasValue = this.value !== '' && this.value != null;
            const caretAtEnd = caretStart === caretEnd && caretEnd === this.value.length;
            if (e.key === 'Backspace' && this.value === '' && prevInputId) {
                e.preventDefault();
                setTimeout(function () {
                    const prevInput = document.getElementById(prevInputId);
                    if (prevInput) {
                        prevInput.focus();
                        prevInput.select();
                    }
                }, 50);
                return;
            }

            // caret position at end and digit entered
            if (isDigit && hasValue && caretAtEnd) {

                // if at last box, replace current value
                if (!nextInputId) {
                    e.preventDefault();
                    this.value = e.key;
                    this.dispatchEvent(new Event('input', { bubbles: true }));
                    this.dispatchEvent(new Event('change', { bubbles: true }));
                    return;
                }
                e.preventDefault();

                // if there is a next id, set the next box value, focus the next next box if available
                if (nextInputId) {
                    setTimeout(function () {
                        const nextInput = document.getElementById(nextInputId);
                        if (nextInput) {
                            nextInput.value = e.key;
                            nextInput.dispatchEvent(new Event('input', { bubbles: true }));
                            nextInput.dispatchEvent(new Event('change', { bubbles: true }));
                        }

                        const targetId = nextNextInputId || nextInputId;
                        const target = document.getElementById(targetId);
                        if (target) target.focus();
                    }, 50);
                }

                return;
            }

            // replace current value and focus next box if caret not at end
            if (isDigit && hasValue) {
                e.preventDefault();
                this.value = e.key;
                this.dispatchEvent(new Event('input', { bubbles: true }));
                this.dispatchEvent(new Event('change', { bubbles: true }));

                if (nextInputId) {
                    setTimeout(function () {
                        const nextInput = document.getElementById(nextInputId);
                        if (nextInput) {
                            nextInput.focus();
                        }
                    }, 50);
                }
            }
        });

        input.addEventListener('input', function (e) {
            if (this.value !== '' && nextInputId) {
                setTimeout(function () {
                    const nextInput = document.getElementById(nextInputId);
                    if (nextInput) {
                        nextInput.focus();
                    }
                }, 50);
            }
        });
    }
};

// ---- Main Formatter ----
window.cardFormatter = {
    attach: function (parentElement) {
        const input = parentElement.querySelector(".cardnumberclass input");
        if (!input) return;

        // Block extra input at max length, but allow replacements
        input.addEventListener("beforeinput", function (e) {
            if (e.isComposing) return; // let IME compose

            const raw = input.value;
            const currDigits = digitsCount(raw);
            const selStart = input.selectionStart ?? 0;
            const selEnd = input.selectionEnd ?? selStart;
            const selectedDigits = digitsCount(raw.slice(selStart, selEnd));

            const maxDigits = getMaxDigits(raw.replace(/\D/g, ""));

            // Handle typed characters
            if (e.inputType === "insertText") {
                const insertDigits = digitsCount(e.data || "");

                // Block non-digits immediately
                if (insertDigits === 0) {
                    e.preventDefault();
                    return;
                }

                // Would this insert exceed max after accounting for selected digits?
                const projected = currDigits - selectedDigits + insertDigits;
                if (projected > maxDigits) {
                    e.preventDefault();
                }
                return;
            }

            // Optional: be strict with paste/drop when already full and not replacing digits
            // (We can't reliably know paste digits here across all browsers, so we only block
            // when no digits are selected; overflow will still be trimmed in 'input'.)
            if (
                /^insertFrom/.test(e.inputType) &&
                selectedDigits === 0 &&
                currDigits >= maxDigits
            ) {
                e.preventDefault();
            }
        });

        // Format + caret logic
        input.addEventListener("input", function (e) {
            const rawValue = e.target.value;              // post-change raw
            const cursorPos = e.target.selectionStart;    // post-change caret

            // Strip to digits and compute max
            let digitsOnly = rawValue.replace(/\D/g, "");
            const maxDigits = getMaxDigits(digitsOnly);

            // Trim overflow (e.g., paste)
            digitsOnly = enforceMaxDigits(digitsOnly, maxDigits);

            // Format
            const formatted = formatCardNumber(digitsOnly, maxDigits === 15);
            e.target.value = formatted;

            // Recalculate caret to follow the same digit index
            const newCursorPos = calculateCursorPosition(rawValue, formatted, cursorPos, maxDigits);
            e.target.setSelectionRange(newCursorPos, newCursorPos);
        });
    }
};
